import React, { useState, SetStateAction, Dispatch } from 'react';
import { useDropzone, FileWithPath } from 'react-dropzone';
import axios from 'axios';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgressWithLabel from '../../Components/CustomUIElements/CircularProgressWithLabel';
import { humanFileSize } from './humanFileSize';
import DocTypeIcon from './DocTypeIcon';
import ClearIcon from '@mui/icons-material/Clear';
import { classes } from './styles';

export default function SingleAttachmentSelector({
  performUpload,
  apiKey,
  uploadUrl,
  onUploadComplete,
  setFile,
  file,
  maxSize
}: {
  performUpload: boolean;
  apiKey?: string;
  uploadUrl?: string;
  onUploadComplete?: () => void;
  setFile?: (file: FileWithPath) => void;
  file?: FileWithPath | null;
  maxSize: number;
}) {
  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxSize,
    maxFiles: 1,
    onDragOver: () => {
      setIsDraggedOver(true);
    },
    onDragLeave: () => {
      setIsDraggedOver(false);
    },
    onDropRejected: () => {
      setIsRejected(true);
    },
    onDrop: (acceptedFile: FileWithPath[]) => {
      if (performUpload) {
        uploadFiles(acceptedFile[0]);
      }
      if (setFile) {
        setFile(acceptedFile[0]);
      }
      setIsDraggedOver(false);
    }
  });

  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [percentCompleted, setPercentCompleted] = useState(0);
  const [error, setError] = useState('');
  const [isHovered, setIsHovered] = useState(false);

  const uploadFiles = async (file: FileWithPath) => {
    if (!apiKey || !uploadUrl || !onUploadComplete) {
      console.error(
        'Missing required parameters for AttachmentSelector component. If performUpload is true, please provide apiKey, uploadUrl, and onUploadComplete.'
      );
      return;
    }
    setIsLoading(true);
    const formData = new FormData();
    formData.append('asset', file);
    try {
      const response = await axios.post(uploadUrl, formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'X-api-authenticate': apiKey },
        onUploadProgress: (progressEvent) => {
          setPercentCompleted(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        }
      });
      if (response.status === 201) {
        setError('');
        onUploadComplete();
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        error.response.data.errors === 'Invalid file.'
          ? setError('Unsupported file type')
          : setError(error.response.data.errors);
      }
      console.log(error);
    } finally {
      setIsRejected(false);
      setIsLoading(false);
      setPercentCompleted(0);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        {...getRootProps()}
        sx={{
          ...classes.dragAreaSingle,
          ...(isDraggedOver ? classes.dragAreaOverSingle : {}),
          ...(file ? { cursor: 'pointer' } : {})
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <input {...getInputProps()} />
        {!isLoading && file && (
          <Box
            sx={classes.fileIconContainer}
            onClick={() => {
              if (!isHovered) return;
              setFile && setFile(null);
            }}
          >
            <Box sx={classes.fileIcon}>
              {isHovered ? (
                <ClearIcon sx={{ '& svg': { color: '#D6827D !important' } }} />
              ) : (
                DocTypeIcon(file.name.split('.').pop())
              )}
            </Box>
            <Box>
              <Box sx={classes.fileName}>
                {isHovered ? 'Click to change attachment' : file.name}
              </Box>
              <Box sx={classes.fileSize}>{humanFileSize(file.size)}</Box>
            </Box>
          </Box>
        )}
        {isLoading ? (
          <>
            <CircularProgressWithLabel value={percentCompleted} size={30} />
            <Box sx={{ marginTop: '4px' }}>Uploading...</Box>
          </>
        ) : (
          !file && (
            <>
              <Box>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="62px"
                  height="39px"
                  viewBox="0 0 61 39"
                  version="1.1"
                >
                  <g id="surface1">
                    <path
                      style={{
                        stroke: 'none',
                        fillRule: 'nonzero',
                        fill: 'rgb(51.372549%,51.372549%,51.372549%)',
                        fillOpacity: 1
                      }}
                      d="M 34.328125 38.65625 C 34.203125 38.402344 33.976562 38.148438 33.972656 37.894531 C 33.933594 36.039062 33.921875 34.1875 33.9375 32.332031 C 33.953125 30.957031 34.179688 30.757812 35.59375 30.746094 C 36.800781 30.734375 38.011719 30.796875 39.214844 30.714844 C 39.691406 30.707031 40.140625 30.488281 40.441406 30.117188 C 40.535156 29.636719 40.421875 29.140625 40.132812 28.746094 C 39.742188 28.242188 39.304688 27.78125 38.820312 27.367188 C 36.371094 24.898438 33.921875 22.4375 31.46875 19.972656 C 30.523438 19.023438 30.105469 19.042969 29.175781 19.984375 C 26.492188 22.710938 23.800781 25.429688 21.109375 28.148438 C 20.996094 28.261719 20.828125 28.355469 20.785156 28.492188 C 20.617188 28.976562 20.261719 29.566406 20.414062 29.9375 C 20.699219 30.375 21.167969 30.660156 21.683594 30.710938 C 22.820312 30.8125 23.96875 30.710938 25.113281 30.714844 C 26.566406 30.722656 26.710938 30.875 26.710938 32.300781 C 26.707031 34.058594 26.675781 35.816406 26.722656 37.574219 C 26.742188 38.382812 26.503906 38.722656 25.648438 38.71875 C 21.167969 38.6875 16.683594 38.769531 12.203125 38.6875 C 5.507812 38.578125 0.105469 33.140625 0.00390625 26.402344 C -0.160156 20.078125 3.519531 14.664062 9.199219 13.011719 C 10.328125 12.683594 11.5 12.535156 12.671875 12.574219 C 13.535156 12.71875 14.359375 12.160156 14.554688 11.304688 C 15.5625 8.667969 17.164062 6.296875 19.230469 4.382812 C 26.757812 -2.6875 38.09375 -1 43.296875 7.980469 C 43.792969 8.835938 44.234375 9.726562 44.644531 10.625 C 44.769531 11.097656 45.246094 11.382812 45.714844 11.269531 C 49.65625 10.855469 52.5 13.699219 52.09375 17.660156 C 52 18.550781 52.453125 18.640625 53.085938 18.792969 C 57.859375 19.820312 61.160156 24.210938 60.84375 29.113281 C 60.472656 33.945312 56.832031 37.878906 52.066406 38.605469 C 51.875 38.621094 51.6875 38.628906 51.496094 38.625 L 50.523438 38.429688 C 51.761719 38.171875 53.074219 38.085938 54.222656 37.613281 C 58.082031 36.003906 60.445312 32.050781 60.050781 27.867188 C 59.640625 23.515625 56.429688 19.957031 52.167969 19.121094 C 51.328125 18.96875 51.1875 18.699219 51.289062 17.882812 C 51.757812 14.074219 48.898438 11.214844 45.195312 11.734375 C 44.695312 11.859375 44.179688 11.589844 43.996094 11.105469 C 43.132812 9.363281 42.140625 7.691406 41.03125 6.09375 C 38.53125 2.78125 34.722656 0.726562 30.59375 0.464844 C 26.46875 0.203125 22.429688 1.757812 19.535156 4.726562 C 17.464844 6.867188 15.878906 9.425781 14.878906 12.234375 C 14.8125 12.53125 14.628906 12.785156 14.367188 12.933594 C 14.109375 13.085938 13.796875 13.121094 13.511719 13.03125 C 11.125 12.679688 8.691406 13.214844 6.667969 14.535156 C 1.140625 17.976562 -0.851562 25.476562 2.195312 31.539062 C 4.191406 35.496094 7.339844 38.019531 11.832031 38.226562 C 16.207031 38.433594 20.601562 38.273438 24.988281 38.296875 C 25.671875 38.296875 25.96875 38.078125 25.957031 37.355469 C 25.933594 35.695312 25.9375 34.03125 25.984375 32.367188 C 26.011719 31.464844 25.675781 31.085938 24.757812 31.132812 C 23.644531 31.191406 22.53125 31.160156 21.417969 31.167969 C 20.683594 31.171875 20 31.027344 19.632812 30.308594 C 19.230469 29.539062 19.714844 28.964844 20.144531 28.402344 C 20.359375 28.125 20.59375 27.867188 20.847656 27.621094 C 23.019531 25.464844 25.191406 23.308594 27.367188 21.15625 C 27.964844 20.496094 28.601562 19.875 29.273438 19.292969 C 30.34375 18.472656 31.101562 18.570312 32.058594 19.519531 C 33.078125 20.527344 34.078125 21.558594 35.089844 22.574219 C 36.976562 24.476562 38.878906 26.359375 40.746094 28.277344 C 41.859375 29.417969 41.476562 30.675781 39.925781 31.042969 C 39.582031 31.113281 39.234375 31.144531 38.882812 31.140625 C 37.867188 31.152344 36.847656 31.183594 35.832031 31.128906 C 35.023438 31.089844 34.691406 31.34375 34.722656 32.210938 C 34.777344 33.839844 34.785156 35.476562 34.722656 37.101562 C 34.6875 38.035156 35.019531 38.308594 35.925781 38.300781 C 39.800781 38.261719 43.683594 38.28125 47.558594 38.292969 C 48 38.316406 48.441406 38.359375 48.875 38.429688 C 44.496094 38.4375 40.113281 38.4375 35.730469 38.460938 C 35.265625 38.492188 34.796875 38.554688 34.339844 38.652344 "
                    />
                    <path
                      style={{
                        stroke: 'none',
                        fillRule: 'nonzero',
                        fill: 'rgb(46.27451%,46.27451%,46.27451%)',
                        fillOpacity: 1
                      }}
                      d="M 34.328125 38.65625 C 34.789062 38.558594 35.253906 38.496094 35.722656 38.464844 C 40.105469 38.441406 44.484375 38.441406 48.867188 38.433594 L 50.527344 38.433594 L 51.496094 38.625 C 49.886719 38.648438 48.277344 38.691406 46.667969 38.691406 C 42.554688 38.691406 38.441406 38.667969 34.328125 38.65625 "
                    />
                    <path
                      style={{
                        stroke: 'none',
                        fillRule: 'nonzero',
                        fill: 'rgb(100%,100%,100%)',
                        fillOpacity: 1
                      }}
                      d="M 50.527344 38.433594 L 48.867188 38.433594 C 48.429688 38.363281 47.992188 38.320312 47.550781 38.296875 C 43.671875 38.285156 39.792969 38.265625 35.914062 38.304688 C 35.007812 38.3125 34.675781 38.039062 34.710938 37.105469 C 34.773438 35.480469 34.769531 33.84375 34.710938 32.214844 C 34.679688 31.347656 35.011719 31.09375 35.824219 31.132812 C 36.839844 31.1875 37.859375 31.15625 38.875 31.144531 C 39.222656 31.148438 39.574219 31.117188 39.914062 31.046875 C 41.464844 30.679688 41.851562 29.421875 40.738281 28.28125 C 38.867188 26.363281 36.964844 24.480469 35.078125 22.578125 C 34.070312 21.5625 33.070312 20.53125 32.050781 19.523438 C 31.09375 18.574219 30.335938 18.476562 29.265625 19.296875 C 28.589844 19.878906 27.953125 20.5 27.355469 21.160156 C 25.183594 23.316406 23.011719 25.46875 20.839844 27.625 C 20.585938 27.871094 20.351562 28.128906 20.136719 28.40625 C 19.707031 28.964844 19.222656 29.542969 19.621094 30.3125 C 19.992188 31.03125 20.675781 31.175781 21.410156 31.171875 C 22.523438 31.164062 23.636719 31.195312 24.746094 31.136719 C 25.667969 31.089844 26 31.464844 25.976562 32.371094 C 25.929688 34.035156 25.925781 35.695312 25.949219 37.359375 C 25.960938 38.082031 25.664062 38.300781 24.980469 38.296875 C 20.59375 38.277344 16.199219 38.4375 11.824219 38.230469 C 7.332031 38.019531 4.183594 35.503906 2.1875 31.539062 C -0.863281 25.480469 1.128906 17.980469 6.660156 14.539062 C 8.683594 13.21875 11.113281 12.683594 13.5 13.035156 C 13.789062 13.125 14.097656 13.089844 14.359375 12.9375 C 14.617188 12.789062 14.804688 12.535156 14.871094 12.238281 C 15.867188 9.429688 17.457031 6.867188 19.527344 4.730469 C 22.421875 1.761719 26.457031 0.207031 30.585938 0.46875 C 34.710938 0.730469 38.523438 2.785156 41.023438 6.097656 C 42.132812 7.691406 43.125 9.367188 43.988281 11.109375 C 44.167969 11.59375 44.683594 11.863281 45.183594 11.734375 C 48.890625 11.21875 51.746094 14.078125 51.28125 17.886719 C 51.179688 18.703125 51.316406 18.976562 52.160156 19.125 C 56.425781 19.960938 59.636719 23.519531 60.046875 27.875 C 60.4375 32.058594 58.074219 36.011719 54.214844 37.621094 C 53.066406 38.09375 51.753906 38.175781 50.519531 38.4375 "
                    />
                  </g>
                </svg>
              </Box>
              {!isDraggedOver ? (
                <Box sx={{ display: 'flex', columnGap: '10px', width: '182px' }}>
                  {!isRejected && !error ? (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>Drag file here or</Box>
                  ) : (
                    <Box sx={{ color: '#D6827D' }}>
                      {isRejected ? 'Files must be under 50mb' : error}
                    </Box>
                  )}
                  <Button sx={classes.selectFilesButtonSingle} onClick={open}>
                    Choose file
                  </Button>
                </Box>
              ) : (
                <Box sx={{ width: '182px', textAlign: 'center' }}>
                  {performUpload ? 'Drop file here to upload!' : 'Drop file here to add!'}
                </Box>
              )}
            </>
          )
        )}
      </Box>
    </Box>
  );
}
